import { z } from 'zod';
import type {
    DefaultSubtypeValues,
    SubtypeProperties,
} from '../../../../../../react-commons/hooks/useSubtypeFormNames';
import { IntSchema } from '../../../../../../react-commons/schemas/integerSchemas';
import { assertUnreachable } from '../../../../../../react-commons/utils/utilFunctions';
import { getMutationSchema } from '../../../../ui/util/getMutationSchema';
import {
    ContentSectionContentType,
    ContentSectionContentTypeSchema,
} from '../../../enumerations/ContentSectionContentType';
import { ResourceType } from '../../../enumerations/ResourceType';
import { useIndirectlyVersionedResourceAdapter } from '../../../resource-base/adapters/useIndirectlyVersionedResourceAdapter';
import { useMultiOrderedResourceSingleParentedResourceAdapterActions } from '../../../resource-base/adapters/useMultiOrderedResourceSingleParentedResourceAdapterActions';
import {
    defaultIndirectlyVersionedArtifactResource,
    IndirectlyVersionedArtifactResourceSchema,
} from '../../../resource-base/artifactResourceTypes';
import { IdSchema } from '../../../resource-base/baseSchemas';
import { msObject } from '../../../resource-base/msObject';
import { copyOverExistingProperties } from '../../utilFunctions';

export enum ContentSectionContentPropertyValidation {
    TYPE = 'TYPE',
    TEXT = 'TEXT',
    IMAGE = 'IMAGE',
    VIDEO = 'VIDEO',
    URL = 'URL',
    DECORATOR = 'DECORATOR',
}

export const ContentSectionDecoratorSchema = msObject({
    weight: IntSchema,
    height: IntSchema,
});

const ContentSectionContentResourceBaseSchema = IndirectlyVersionedArtifactResourceSchema.extend({
    type: ContentSectionContentTypeSchema,
    decorator: ContentSectionDecoratorSchema,
    resourceType: z.literal(ResourceType.CONTENT_SECTION_CONTENT).default(ResourceType.CONTENT_SECTION_CONTENT),
});

const ImageAttachmentContentSectionContentResourceSchema = ContentSectionContentResourceBaseSchema.extend({
    type: z.literal(ContentSectionContentType.IMAGE_ATTACHMENT),
    image: IdSchema,
});

const VideoAttachmentContentSectionContentResourceSchema = ContentSectionContentResourceBaseSchema.extend({
    type: z.literal(ContentSectionContentType.VIDEO_ATTACHMENT),
    video: IdSchema,
});

const VideoUrlContentSectionContentResourceSchema = ContentSectionContentResourceBaseSchema.extend({
    type: z.literal(ContentSectionContentType.VIDEO_URL),
    url: z.string().nullable(),
});

const PlainTextContentSectionContentResourceSchema = ContentSectionContentResourceBaseSchema.extend({
    type: z.literal(ContentSectionContentType.PLAIN_TEXT),
    text: z.string(),
});

const RichTextContentSectionContentResourceSchema = ContentSectionContentResourceBaseSchema.extend({
    type: z.literal(ContentSectionContentType.RICH_TEXT),
    text: z.string(),
});

const MarkdownContentSectionContentResourceSchema = ContentSectionContentResourceBaseSchema.extend({
    type: z.literal(ContentSectionContentType.MARKDOWN),
    text: z.string(),
});

export const ContentSectionContentResourceSchema = z.discriminatedUnion('type', [
    ImageAttachmentContentSectionContentResourceSchema,
    VideoAttachmentContentSectionContentResourceSchema,
    VideoUrlContentSectionContentResourceSchema,
    PlainTextContentSectionContentResourceSchema,
    RichTextContentSectionContentResourceSchema,
    MarkdownContentSectionContentResourceSchema,
]);

export const ContentSectionContentMutationResourceSchema = z.discriminatedUnion('type', [
    getMutationSchema(ImageAttachmentContentSectionContentResourceSchema),
    getMutationSchema(VideoAttachmentContentSectionContentResourceSchema),
    getMutationSchema(VideoUrlContentSectionContentResourceSchema),
    getMutationSchema(PlainTextContentSectionContentResourceSchema),
    getMutationSchema(RichTextContentSectionContentResourceSchema),
    getMutationSchema(MarkdownContentSectionContentResourceSchema),
]);

export type ContentSectionContentResource = z.infer<typeof ContentSectionContentResourceSchema>;

export const contentSectionContentDefaultSubtypeValues: DefaultSubtypeValues<ContentSectionContentResource> = {
    text: '',
    image: '',
    video: '',
    url: null,
};

export const contentSectionContentSubTypeProperties: SubtypeProperties<ContentSectionContentResource, 'type'> = {
    [ContentSectionContentType.IMAGE_ATTACHMENT]: {
        image: 'image',
    },
    [ContentSectionContentType.VIDEO_ATTACHMENT]: {
        video: 'video',
    },
    [ContentSectionContentType.VIDEO_URL]: {
        url: 'url',
    },
    [ContentSectionContentType.PLAIN_TEXT]: {
        text: 'text',
    },
    [ContentSectionContentType.RICH_TEXT]: {
        text: 'text',
    },
    [ContentSectionContentType.MARKDOWN]: {
        text: 'text',
    },
};

export function getDefaultContentSectionContentResource(
    type: ContentSectionContentType,
    previousResource?: ContentSectionContentResource
): ContentSectionContentResource {
    const baseResource: z.infer<typeof ContentSectionContentResourceBaseSchema> = {
        ...defaultIndirectlyVersionedArtifactResource,
        resourceType: ResourceType.CONTENT_SECTION_CONTENT,
        decorator: {
            weight: 1,
            height: 360,
        },
        type,
    };

    switch (type) {
        case ContentSectionContentType.IMAGE_ATTACHMENT: {
            const imageBase = {
                ...baseResource,
                image: '',
            };
            return {
                ...copyOverExistingProperties(imageBase, previousResource),
                type,
            };
        }
        case ContentSectionContentType.VIDEO_ATTACHMENT: {
            const videoBase = {
                ...baseResource,
                video: '',
            };
            return {
                ...copyOverExistingProperties(videoBase, previousResource),
                type,
            };
        }
        case ContentSectionContentType.VIDEO_URL: {
            const urlBase = {
                ...baseResource,
                url: null,
            };
            return {
                ...copyOverExistingProperties(urlBase, previousResource),
                type,
            };
        }
        case ContentSectionContentType.PLAIN_TEXT: {
            const plainBase = {
                ...baseResource,
                text: '',
            };
            return {
                ...copyOverExistingProperties(plainBase, previousResource),
                type,
            };
        }
        case ContentSectionContentType.RICH_TEXT: {
            const richBase = {
                ...baseResource,
                text: '',
            };
            return {
                ...copyOverExistingProperties(richBase, previousResource),
                type,
            };
        }
        case ContentSectionContentType.MARKDOWN: {
            const markBase = {
                ...baseResource,
                text: '',
            };
            return {
                ...copyOverExistingProperties(markBase, previousResource),
                type,
            };
        }
        default: {
            return assertUnreachable(type);
        }
    }
}

export const defaultContentSectionContentResource: ContentSectionContentResource =
    getDefaultContentSectionContentResource(ContentSectionContentType.RICH_TEXT);

export function useContentSectionContentActions() {
    const actions = {
        ...useMultiOrderedResourceSingleParentedResourceAdapterActions(
            'project/content-section-content',
            ResourceType.CONTENT_SECTION_CONTENT,
            ContentSectionContentResourceSchema
        ),
        ...useIndirectlyVersionedResourceAdapter(
            'project/content-section-content',
            ResourceType.CONTENT_SECTION_CONTENT,
            ContentSectionContentResourceSchema
        ),
    };

    return {
        ...actions,
    };
}
